/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import Text from '@/shared/ui/components/basic/Text';
import {
  blockFieldsGridStyle, fieldLabelStyle, fieldValueStyle,
} from './settings-block.styles';

/** One label/value pair in a `fields` mid. */
export interface SettingsField {
  /** Stable key, also the testid suffix. */
  key: string;
  /** Already-localized label. */
  label: string;
  /**
   * The value. Empty, null or undefined means *absent*, and absence is
   * written out rather than elided — see {@link fieldValueStyle}.
   */
  value?: string | null;
  /**
   * True when the field may legitimately be blank, so its absence is written
   * quietly instead of in copper. Copper is reserved for the fields the
   * pending band counts; anything else would promise a chip that never comes.
   */
  optional?: boolean;
}

export interface FieldsBlockProps {
  fields: SettingsField[];
  /**
   * Already-localized wording for an absent value ("não informado").
   * Passed in rather than translated here: this component belongs to the
   * design system and has no business reading a feature's namespace.
   */
  missingLabel: string;
  /** Test id prefix for the individual fields. */
  testIdPrefix?: string;
}

/**
 * Whether a field carries no value and should render as absent.
 *
 * @param field - The field to test.
 * @returns True when the value is empty, null or undefined.
 */
export function isFieldMissing(field: SettingsField): boolean {
  return !field.value || field.value.trim() === '';
}

/**
 * The `fields` mid: a reflowing grid of label/value pairs.
 *
 * Renders nothing at all when there are no fields — an empty grid leaves a
 * phantom rectangle of padding under the header, which reads as a component
 * that failed to load rather than as a block with nothing to say.
 *
 * @param props - Component props.
 * @returns The fields grid, or null when there is nothing to show.
 */
export function FieldsBlock({ fields, missingLabel, testIdPrefix = 'settings-field' }: FieldsBlockProps) {
  if (fields.length === 0) return null;

  return (
    <div style={blockFieldsGridStyle}>
      {fields.map((field) => {
        const missing = isFieldMissing(field);
        return (
          <div
            key={field.key}
            data-testid={`${testIdPrefix}-${field.key}`}
            style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}
          >
            <Text component="span" style={fieldLabelStyle}>{field.label}</Text>
            <Text component="span" style={fieldValueStyle(missing, field.optional)}>
              {missing ? missingLabel : field.value}
            </Text>
          </div>
        );
      })}
    </div>
  );
}
